import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BanknotesIcon,
  HomeIcon,
  CreditCardIcon,
  BuildingLibraryIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  PresentationChartLineIcon,
  AcademicCapIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CalculatorIcon,
  CheckIcon,
  ArrowRightIcon,
  PhoneIcon,
  ChatBubbleLeftIcon,
  QuestionMarkCircleIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  GiftIcon,
  ClockIcon,
  HandRaisedIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import Navigation from '../components/common/Navigation';
import Footer from '../components/common/Footer';
import BackgroundTexture from '../components/common/BackgroundTexture';
import Particles from '../components/common/Particles';

const ProductsPage = () => {
  const [activeTab, setActiveTab] = useState('personal');

  const products = {
    personal: [
      {
        name: 'Premier Checking',
        description: 'A premium checking experience with exclusive benefits',
        icon: BanknotesIcon,
        color: 'text-primary-600',
        gradient: 'from-primary-50 to-white',
        features: [
          'No monthly maintenance fees',
          'Free ATM access nationwide',
          'Early direct deposit access',
          'Mobile check deposit'
        ],
        rate: 'No minimum balance',
        popular: true,
        cta: '/register'
      },
      {
        name: 'High-Yield Savings',
        description: 'Make your money work harder with competitive rates',
        icon: BuildingLibraryIcon,
        color: 'text-primary-600',
        gradient: 'from-primary-50 to-white',
        features: [
          '3.50% APY* on all balances',
          'No monthly fees',
          'Automatic savings tools',
          'Goal-based savings'
        ],
        rate: '3.50% APY',
        cta: '/register'
      },
      {
        name: 'Rewards Credit Card',
        description: 'Earn rewards on every purchase, every day',
        icon: CreditCardIcon,
        color: 'text-primary-600',
        gradient: 'from-primary-50 to-white',
        features: [
          '2% unlimited cash back',
          'No annual fee',
          'Travel insurance included',
          'Mobile wallet compatible'
        ],
        rate: '0% APR for 15 months',
        cta: '/credit-card'
      },
      {
        name: 'Home Mortgages',
        description: 'Your dream home awaits with competitive rates',
        icon: HomeIcon,
        color: 'text-primary-600',
        gradient: 'from-primary-50 to-white',
        features: [
          'Low down payment options',
          'Fixed & adjustable rates',
          'First-time buyer programs',
          'Digital application'
        ],
        rate: 'From 3.99% APR',
        cta: '/mortgages'
      },
      {
        name: 'Investment Solutions',
        description: 'Build your wealth with expert guidance',
        icon: ChartBarIcon,
        color: 'text-primary-600',
        gradient: 'from-primary-50 to-white',
        features: [
          'Retirement planning',
          'Managed portfolios',
          'Tax-advantaged accounts',
          'Professional advice'
        ],
        rate: 'Custom solutions',
        cta: '/investments'
      },
      {
        name: 'Personal Loans',
        description: 'Flexible financing for life\'s moments',
        icon: CurrencyDollarIcon,
        color: 'text-primary-600',
        gradient: 'from-primary-50 to-white',
        features: [
          'Quick approval process',
          'Competitive rates',
          'No prepayment penalties',
          'Fixed monthly payments'
        ],
        rate: 'From 5.99% APR',
        cta: '/loans'
      }
    ],
    business: [
      {
        name: 'Business Checking',
        description: 'Powerful tools for your growing business',
        icon: BanknotesIcon,
        color: 'text-primary-600',
        gradient: 'from-primary-50 to-white',
        features: [
          '500 free transactions',
          'Online & mobile banking',
          'Business debit cards',
          'Integrated payroll'
        ],
        rate: 'First year free',
        popular: true,
        cta: '/business/register'
      },
      {
        name: 'Merchant Services',
        description: 'Accept payments anywhere, anytime',
        icon: CreditCardIcon,
        color: 'text-primary-600',
        gradient: 'from-primary-50 to-white',
        features: [
          'Competitive processing rates',
          'Next-day deposits',
          'Mobile payment solutions',
          'Point-of-sale systems'
        ],
        rate: 'Custom pricing',
        cta: '/merchant-services'
      },
      {
        name: 'Business Credit Card',
        description: 'Streamline expenses and earn rewards',
        icon: CreditCardIcon,
        color: 'text-primary-600',
        gradient: 'from-primary-50 to-white',
        features: [
          '2% cash back on purchases',
          'Employee cards included',
          'Expense management tools',
          'Travel benefits'
        ],
        rate: 'No annual fee',
        cta: '/business/credit-card'
      },
      {
        name: 'Business Loans',
        description: 'Fuel your business growth',
        icon: CurrencyDollarIcon,
        color: 'text-primary-600',
        gradient: 'from-primary-50 to-white',
        features: [
          'SBA loan options',
          'Equipment financing',
          'Line of credit',
          'Commercial real estate'
        ],
        rate: 'From 4.99% APR',
        cta: '/business/loans'
      },
      {
        name: 'Treasury Management',
        description: 'Optimize your cash flow',
        icon: PresentationChartLineIcon,
        color: 'text-primary-600',
        gradient: 'from-primary-50 to-white',
        features: [
          'Cash flow forecasting',
          'Fraud prevention',
          'Payment automation',
          'Reporting tools'
        ],
        rate: 'Custom solutions',
        cta: '/treasury-management'
      },
      {
        name: 'Business Savings',
        description: 'Make your excess cash work harder',
        icon: BuildingLibraryIcon,
        color: 'text-primary-600',
        gradient: 'from-primary-50 to-white',
        features: [
          'High-yield business savings',
          'Money market accounts',
          'CD options',
          'Sweep accounts'
        ],
        rate: 'Up to 2.50% APY',
        cta: '/business/savings'
      }
    ]
  };

  const benefits = [
    {
      icon: ShieldCheckIcon,
      title: 'Secure & Protected',
      description: 'NCUA insured with advanced security'
    },
    {
      icon: ClockIcon,
      title: '24/7 Access',
      description: 'Bank anytime, anywhere with digital banking'
    },
    {
      icon: GiftIcon,
      title: 'Member Rewards',
      description: 'Exclusive benefits for our members'
    },
    {
      icon: HandRaisedIcon,
      title: 'Personal Service',
      description: 'Expert guidance when you need it'
    }
  ];

  const ProductCard = ({ product }) => (
    <div className="relative group">
      {product.popular && (
        <div className="absolute -top-4 -right-4 bg-gradient-to-r from-accent-500 to-accent-600 
          text-white px-6 py-2 rounded-full text-sm font-medium z-10 shadow-lg">
          Most Popular
        </div>
      )}
      <div className="h-full bg-white rounded-2xl shadow-soft hover:shadow-xl 
        transition-all duration-500 overflow-hidden border border-gray-100">
        <div className={`p-8 bg-gradient-to-br ${product.gradient}`}>
          <product.icon className={`h-12 w-12 ${product.color}`} />
          <h3 className="text-2xl font-bold mt-6 text-gray-900">{product.name}</h3>
          <p className="mt-3 text-gray-600">{product.description}</p>
          {product.rate && (
            <div className="mt-6 inline-block bg-primary-50 rounded-full px-4 py-1 
              text-sm font-medium text-primary-600">
              {product.rate}
            </div>
          )}
        </div>
        <div className="p-8">
          <ul className="space-y-4 mb-8">
            {product.features.map((feature, index) => (
              <li key={index} className="flex items-center text-gray-700">
                <CheckIcon className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/contact"
            className="w-full inline-flex items-center justify-center px-6 py-3 rounded-xl 
              bg-primary-600 text-white hover:bg-primary-700 transition-all duration-300 
              group"
          >
            Learn More
            <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <BackgroundTexture />
      <Particles />
      <Navigation />

      {/* Hero Section */}
      <div className="relative pt-24 overflow-hidden bg-gradient-to-br from-primary-600 to-primary-800">
        <div className="absolute inset-0 bg-pattern-dots opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-radial from-primary-400/20 via-transparent to-transparent"></div>
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center relative">
            <div className="absolute -top-20 -left-20 w-40 h-40 bg-primary-300/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-primary-300/20 rounded-full blur-3xl"></div>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 relative z-10 font-serif">
              Financial Solutions for
              <span className="block mt-2 text-accent-300">
                Every Stage of Life
              </span>
            </h1>
            <p className="text-xl text-white/90 leading-relaxed max-w-2xl mx-auto mb-12">
              Discover tailored financial products designed to help you achieve your goals, 
              whether personal or business.
            </p>

            <div className="inline-flex p-1.5 bg-white/10 backdrop-blur-sm rounded-full 
              shadow-xl border border-white/20">
              <button
                onClick={() => setActiveTab('personal')}
                className={`px-8 py-3 rounded-full transition-all duration-300 text-base font-medium
                  ${activeTab === 'personal' 
                    ? 'bg-white text-primary-600 shadow-sm' 
                    : 'text-white hover:bg-white/10'
                  }`}
              >
                Personal Banking
              </button>
              <button
                onClick={() => setActiveTab('business')}
                className={`px-8 py-3 rounded-full transition-all duration-300 text-base font-medium
                  ${activeTab === 'business' 
                    ? 'bg-white text-primary-600 shadow-sm' 
                    : 'text-white hover:bg-white/10'
                  }`}
              >
                Business Banking
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-16 bg-white relative">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start p-6 bg-gradient-to-br from-primary-50 to-white 
                rounded-xl shadow-soft group hover:shadow-lg transition-all duration-300">
                <benefit.icon className="h-8 w-8 text-primary-600 mr-4 group-hover:scale-110 
                  transition-transform" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-serif">
            {activeTab === 'personal' ? 'Personal Banking Solutions' : 'Business Banking Solutions'}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {activeTab === 'personal' 
              ? 'Discover financial solutions designed to help you thrive at every stage of life'
              : 'Power your business growth with our comprehensive suite of financial services'
            }
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products[activeTab].map((product, index) => (
            <ProductCard key={index} product={product} />
          ))}
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 font-serif">Need Help Choosing?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Link to="/contact" className="bg-gradient-to-br from-primary-50 to-white p-6 
                rounded-xl shadow-soft hover:shadow-lg transition-all text-center group">
                <PhoneIcon className="h-8 w-8 text-primary-600 mx-auto mb-4 
                  group-hover:scale-110 transition-transform" />
                <h3 className="font-bold text-gray-900 mb-2">Call Us</h3>
                <p className="text-gray-600 text-sm">Speak with a product specialist</p>
              </Link>
              <Link to="/locations" className="bg-gradient-to-br from-primary-50 to-white p-6 
                rounded-xl shadow-soft hover:shadow-lg transition-all text-center group">
                <BuildingLibraryIcon className="h-8 w-8 text-primary-600 mx-auto mb-4 
                  group-hover:scale-110 transition-transform" />
                <h3 className="font-bold text-gray-900 mb-2">Visit a Branch</h3>
                <p className="text-gray-600 text-sm">Meet us in person</p>
              </Link>
              <Link to="/schedule" className="bg-gradient-to-br from-primary-50 to-white p-6 
                rounded-xl shadow-soft hover:shadow-lg transition-all text-center group">
                <CalendarIcon className="h-8 w-8 text-primary-600 mx-auto mb-4 
                  group-hover:scale-110 transition-transform" />
                <h3 className="font-bold text-gray-900 mb-2">Schedule Meeting</h3>
                <p className="text-gray-600 text-sm">Book a consultation</p>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-gradient-to-br from-primary-600 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-pattern-dots opacity-10"></div>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6 font-serif">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Join First Equity today and experience banking that puts you first. 
              Our team is ready to help you find the perfect financial solutions.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/register"
                className="px-6 py-3 bg-white text-primary-600 font-medium rounded-xl 
                  hover:bg-white/90 transition-all duration-300 hover:-translate-y-1 
                  inline-flex items-center justify-center group shadow-soft"
              >
                Open an Account
                <ArrowTrendingUpIcon className="ml-2 h-5 w-5 transform group-hover:translate-x-1 
                  transition-transform duration-200" />
              </Link>
              <Link
                to="/contact"
                className="px-6 py-3 border-2 border-white text-white font-medium 
                  rounded-xl hover:bg-white/10 transition-all duration-300 
                  hover:-translate-y-1"
              >
                Talk to Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductsPage; 